import React, { useState, useEffect } from 'react';
import {
    CheckCircle2, ChevronDown, ChevronUp, X, ArrowRight,
    Building2, Image, QrCode, Sparkles, Users, Ticket, Bell, Cpu, Rocket, Gift, ShoppingBag
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase/config';
import { menuService } from '../../services/menuService';

const STORAGE_KEY = 'ugra_setup_wizard_dismissed_';

const SetupWizard = ({ tenantData, effectiveTenantId, customers, promotions, notifications }) => {

    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [menuState, setMenuState] = useState(null); // null = loading, {hasTemplate, hasItems}

    // Check menu existence
    useEffect(() => {
        if (!effectiveTenantId) return;
        menuService.fetchMenu(effectiveTenantId)
            .then(menuData => {
                if (!menuData) {
                    setMenuState({ hasTemplate: false, hasItems: false });
                    return;
                }
                const cats = menuData.categories || [];
                const hasItems = Array.isArray(cats) && cats.some(c => Array.isArray(c.items) && c.items.length > 0);
                setMenuState({ hasTemplate: true, hasItems });
            })
            .catch(() => {
                setMenuState({ hasTemplate: false, hasItems: false });
            });
    }, [effectiveTenantId]);

    // Persist dismissed per tenant
    useEffect(() => {
        if (!effectiveTenantId) return;
        const val = localStorage.getItem(STORAGE_KEY + effectiveTenantId);
        setDismissed(val === 'true');

        const handleShow = () => {
            setDismissed(false);
            setCollapsed(false);
            localStorage.setItem(STORAGE_KEY + effectiveTenantId, 'false');
        };
        window.addEventListener('show-setup-wizard', handleShow);
        return () => window.removeEventListener('show-setup-wizard', handleShow);
    }, [effectiveTenantId]);

    const handleDismiss = () => {
        setDismissed(true);
        localStorage.setItem(STORAGE_KEY + effectiveTenantId, 'true');
    };

    // Removed loyaltyCustomized check here since it's no longer used

    const steps = {
        required: [
            {
                id: 'business_profile',
                title: 'İşletme Profili',
                desc: 'Adres, telefon ve iletişim bilgilerini ekleyin',
                icon: Building2,
                color: '#3b82f6',
                done: !!(tenantData?.address && tenantData?.phone),
                action: () => navigate('/app/settings'),
                actionLabel: 'Profili Düzenle'
            },
            {
                id: 'logo_banner',
                title: 'Logo & Banner',
                desc: 'İşletmenizi temsil eden logo ve kapak görselini yükleyin',
                icon: Image,
                color: '#8b5cf6',
                done: !!(tenantData?.theme?.logoUrl && tenantData?.banner_url),
                action: () => navigate('/app/settings'),
                actionLabel: 'Görsel Yükle'
            },
            {
                id: 'menu_template',
                title: 'QR Menü Şablonu Oluştur',
                desc: 'Müşterilerin QR ile göreceği menü şablonunu hazırlayın',
                icon: QrCode,
                color: '#f59e0b',
                done: menuState !== null && menuState.hasTemplate,
                loading: menuState === null,
                action: () => navigate('/app/menu'),
                actionLabel: 'Menü Oluştur'
            },
            {
                id: 'add_product',
                title: 'Ürün Ekle',
                desc: 'Menünüze en az bir kategori ve ürün ekleyin',
                icon: ShoppingBag,
                color: '#10b981',
                done: menuState !== null && menuState.hasItems,
                loading: menuState === null,
                action: () => navigate('/app/menu'),
                actionLabel: 'Ürün Ekle'
            }
        ],
        optional: [
            {
                id: 'table_qrs',
                title: 'Masa QR\'ları Oluştur',
                desc: 'Masalardan sipariş ve check-in işlemlerini otomatikleştirmek için masalara özel QR kodlar oluşturun',
                icon: QrCode,
                color: '#ec4899',
                done: (tenantData?.tables || []).length > 0,
                action: () => navigate('/app/settings/qr'),
                actionLabel: 'Masa Oluştur'
            },
            {
                id: 'first_customer',
                title: 'İlk Müşteriyi Ekle',
                desc: 'Manuel müşteri kaydı oluşturun veya QR ile check-in alın',
                icon: Users,
                color: '#3b82f6',
                done: (customers || []).length > 0,
                action: () => navigate('/app/customers'),
                actionLabel: 'Müşteri Ekle'
            },
            {
                id: 'reward',
                title: 'Ödül Oluştur',
                desc: 'Müşterilerin puan karşılığı alabileceği bir ödül tanımlayın',
                icon: Gift,
                color: '#f97316',
                done: (promotions || []).some(p => p.isReward),
                action: () => navigate('/app/campaigns'),
                actionLabel: 'Ödül Oluştur'
            },
            {
                id: 'campaign',
                title: 'Kampanya Oluştur',
                desc: 'Müşterilerinizi geri kazanmak için indirim kampanyası tanımlayın',
                icon: Ticket,
                color: '#f59e0b',
                done: (promotions || []).some(p => !p.isReward),
                action: () => navigate('/app/campaigns'),
                actionLabel: 'Kampanya Oluştur'
            },
            {
                id: 'notification',
                title: 'Duyuru Gönder',
                desc: 'Müşterilerinize ilk kampanya, haber veya push bildiriminizi gönderin',
                icon: Bell,
                color: '#ef4444',
                done: (notifications || []).length > 0,
                action: () => navigate('/app/notifications'),
                actionLabel: 'Duyuru Oluştur'
            },
            {
                id: 'integration',
                title: 'POS Entegrasyonu',
                desc: 'Ödeal veya başka bir POS cihazınızı sisteme bağlayın',
                icon: Cpu,
                color: '#6366f1',
                done: !!(tenantData?.integrations?.pos || tenantData?.pos_integration),
                action: () => navigate('/app/settings'),
                actionLabel: 'Entegrasyon Bağla'
            }
        ]
    };

    const requiredDone = steps.required.filter(s => s.done).length;
    const optionalDone = steps.optional.filter(s => s.done).length;
    const totalDone = requiredDone + optionalDone;
    const total = steps.required.length + steps.optional.length;
    const allRequiredDone = requiredDone === steps.required.length;
    const progressPct = Math.round((totalDone / total) * 100);

    if (dismissed) return null;

    return (
        <div style={{
            marginBottom: '2rem',
            borderRadius: '20px',
            border: '1px solid var(--border)',
            background: 'var(--bg-card)',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.05)'
        }}>
            {/* Header */}
            <div style={{
                padding: '1.25rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                borderBottom: collapsed ? 'none' : '1px solid var(--border)',
                cursor: 'pointer',
                background: allRequiredDone
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, transparent 100%)'
                    : 'linear-gradient(135deg, rgba(var(--primary-rgb),0.06) 0%, transparent 100%)'
            }} onClick={() => setCollapsed(c => !c)}>
                <div style={{
                    width: '44px', height: '44px', borderRadius: '14px', flexShrink: 0,
                    background: allRequiredDone ? 'rgba(16,185,129,0.12)' : 'rgba(var(--primary-rgb),0.1)',
                    color: allRequiredDone ? '#10b981' : 'var(--primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <Rocket size={22} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: '1rem' }}>
                            {allRequiredDone ? 'Harika! Zorunlu kurulum tamamlandı 🎉' : 'Kurulum Sihirbazı'}
                        </span>
                        <span style={{
                            fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px',
                            borderRadius: '20px',
                            background: allRequiredDone ? 'rgba(16,185,129,0.12)' : 'rgba(var(--primary-rgb),0.1)',
                            color: allRequiredDone ? '#10b981' : 'var(--primary)'
                        }}>
                            {totalDone}/{total} tamamlandı
                        </span>
                    </div>
                    {/* Progress bar */}
                    <div style={{ marginTop: '8px', height: '6px', borderRadius: '99px', background: 'var(--border)', overflow: 'hidden', maxWidth: '320px' }}>
                        <div style={{
                            height: '100%', borderRadius: '99px',
                            width: `${progressPct}%`,
                            background: allRequiredDone
                                ? 'linear-gradient(90deg, #10b981, #34d399)'
                                : 'linear-gradient(90deg, var(--primary), #d97706)',
                            transition: 'width 0.5s ease'
                        }} />
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                        onClick={e => { e.stopPropagation(); setCollapsed(c => !c); }}
                        style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: 'var(--text-muted)', padding: '6px', borderRadius: '8px',
                            display: 'flex', alignItems: 'center'
                        }}
                        title={collapsed ? 'Genişlet' : 'Daralt'}
                    >
                        {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                    </button>
                    <button
                        onClick={e => { e.stopPropagation(); handleDismiss(); }}
                        style={{
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: 'var(--text-muted)', padding: '6px', borderRadius: '8px',
                            display: 'flex', alignItems: 'center'
                        }}
                        title="Kapat"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Steps */}
            {!collapsed && (
                <div style={{ padding: '1.5rem' }}>
                    {/* Required */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            marginBottom: '1rem'
                        }}>
                            <span style={{
                                fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em',
                                textTransform: 'uppercase', color: '#ef4444',
                                background: 'rgba(239,68,68,0.08)', padding: '3px 10px', borderRadius: '20px'
                            }}>Zorunlu</span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                {requiredDone}/{steps.required.length} tamamlandı
                            </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '0.75rem' }}>
                            {steps.required.map(step => (
                                <StepCard key={step.id} step={step} />
                            ))}
                        </div>
                    </div>

                    {/* Optional */}
                    <div>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            marginBottom: '1rem'
                        }}>
                            <span style={{
                                fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em',
                                textTransform: 'uppercase', color: '#6366f1',
                                background: 'rgba(99,102,241,0.08)', padding: '3px 10px', borderRadius: '20px'
                            }}>İsteğe Bağlı</span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                {optionalDone}/{steps.optional.length} tamamlandı
                            </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '0.75rem' }}>
                            {steps.optional.map(step => (
                                <StepCard key={step.id} step={step} compact />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .setup-step-card { transition: all 0.2s ease; }
                .setup-step-card:hover { border-color: var(--primary) !important; transform: translateY(-2px); }
                .setup-step-card.done:hover { border-color: #10b981 !important; }
                .setup-action-btn { transition: all 0.15s ease; }
                .setup-action-btn:hover { opacity: 0.85; transform: scale(0.97); }
            `}</style>
        </div>
    );
};

const StepCard = ({ step, compact }) => {
    const Icon = step.icon;

    return (
        <div
            className={`setup-step-card ${step.done ? 'done' : ''}`}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.875rem',
                padding: compact ? '0.875rem 1rem' : '1rem 1.125rem',
                borderRadius: '14px',
                border: `1px solid ${step.done ? 'rgba(16,185,129,0.25)' : 'var(--border)'}`,
                background: step.done ? 'rgba(16,185,129,0.04)' : 'var(--bg-main)',
                opacity: step.done ? 0.8 : 1
            }}
        >
            {/* Icon */}
            <div style={{
                width: compact ? '36px' : '40px',
                height: compact ? '36px' : '40px',
                borderRadius: '10px',
                flexShrink: 0,
                background: step.done ? 'rgba(16,185,129,0.1)' : `${step.color}15`,
                color: step.done ? '#10b981' : step.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                {step.done ? <CheckCircle2 size={compact ? 18 : 20} /> : <Icon size={compact ? 18 : 20} />}
            </div>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontWeight: 700,
                    fontSize: compact ? '0.8125rem' : '0.875rem',
                    marginBottom: '2px',
                    color: step.done ? 'var(--text-muted)' : 'var(--text-main)',
                    textDecoration: step.done ? 'line-through' : 'none'
                }}>
                    {step.title}
                    {step.loading && (
                        <span style={{ fontSize: '0.65rem', marginLeft: '6px', color: 'var(--text-muted)' }}>kontrol ediliyor…</span>
                    )}
                </div>
                {!compact && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.45, margin: 0 }}>
                        {step.desc}
                    </p>
                )}
                {compact && step.done && (
                    <p style={{ fontSize: '0.7rem', color: '#10b981', margin: 0, fontWeight: 600 }}>Tamamlandı</p>
                )}
                {compact && !step.done && (
                    <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 }}>{step.desc}</p>
                )}
            </div>

            {/* Action */}
            {!step.done && (
                <button
                    className="setup-action-btn"
                    onClick={step.action}
                    style={{
                        flexShrink: 0,
                        background: step.color,
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: compact ? '6px 10px' : '7px 12px',
                        fontSize: compact ? '0.7rem' : '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {compact ? <ArrowRight size={14} /> : <>{step.actionLabel} <ArrowRight size={13} /></>}
                </button>
            )}
        </div>
    );
};

export default SetupWizard;
